/**
 * Il nome leggibile di una scommessa aperta, dal mercato come lo chiama il bot.
 *
 * Il bot nomina i mercati per chiave di registro — `OU2.5`, `OU1H1.5`, `TTA0.5`,
 * `BTTS2H`, `EXACT_SCORE` — e l'esito per coordinata (`over`, `yes`, `no`,
 * `event_team`). Sono nomi giusti per il codice e inutilizzabili per chi deve
 * decidere in dieci secondi se uscire da una partita: la pagina Posizioni scrive
 * "Over 2.5" e "Gol / Gol · 1° tempo", nello stesso vocabolario della pagina
 * Quote (`nomeMercato`) e delle lavagne dei bookmaker.
 *
 * Un mercato che non si sa leggere torna com'è scritto (`KIND · esito`) invece
 * di un nome inventato: la chiave grezza è brutta ma vera, e si riconosce.
 */

/** `OU2.5` → {base: "OU", periodo: "", linea: "2.5"}; `TTA1H0.5` → {base: "TTA", periodo: "1H", linea: "0.5"}. */
const scomponi = (kind: string): { base: string; periodo: string; linea: string } | null => {
  const m = /^([A-Z_]+?)(1H|2H)?(\d+(?:\.\d+)?)?$/.exec(kind);
  return m ? { base: m[1]!, periodo: m[2] ?? "", linea: m[3] ?? "" } : null;
};

const TEMPO: Record<string, string> = { "1H": " · 1° tempo", "2H": " · 2° tempo" };

const ESITO_1X2: Record<string, string> = {
  home: "1 (Casa)",
  draw: "X (Pareggio)",
  away: "2 (Ospiti)",
  // la regola compra "la squadra dell'evento": quale sia dipende dal gol che
  // l'ha fatta scattare, e lo snapshot non lo porta — meglio dirlo che tirare a indovinare
  event_team: "squadra dell'evento",
  other_team: "squadra avversaria",
};

/**
 * `betName("OU2.5", "over")` → `"Over 2.5"`.
 * `null` quando il mercato non si conosce affatto (nessun fill leggibile sul
 * token): chi chiama mostra il token, non un nome vuoto.
 */
export function betName(kind: string | null | undefined, outcome: string | null | undefined): string | null {
  if (!kind) return null;
  const esito = outcome ?? "";
  const parti = scomponi(kind);
  const tempo = TEMPO[parti?.periodo ?? ""] ?? "";

  switch (parti?.base) {
    case "OU": {
      const verso = esito === "under" ? "Under" : "Over";
      return `${verso} ${parti.linea || "(linea principale)"}${tempo}`;
    }
    case "TTH":
    case "TTA": {
      const chi = parti.base === "TTH" ? "Casa" : "Ospiti";
      const verso = esito === "under" ? "Under" : "Over";
      return `${chi} ${verso} ${parti.linea}${tempo}`;
    }
    case "BTTS":
      return `${esito === "no" ? "No Gol" : "Gol / Gol"}${tempo}`;
    case "WINNER":
      return `Esito finale · ${ESITO_1X2[esito] ?? esito}`;
    case "FTS":
      return `Prima rete · ${esito === "neither" ? "nessuno" : (ESITO_1X2[esito] ?? esito)}`;
    case "EXACT_SCORE":
      return `Punteggio esatto · ${esito === "no" ? "no" : esito}`;
    default:
      return esito ? `${kind} · ${esito}` : kind;
  }
}
